import React, { useState, useRef, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Play, ChevronDown, Film, Video, Edit, Lightbulb, Pencil, Camera, Clapperboard, Sparkles } from 'lucide-react';
import { MEDIA_CONFIG } from '../data/media';
import ContactButton from '../components/ContactButton';
import VideoPlayer from '../components/VideoPlayer';

const PROCESS_STEPS = [
  {
    number: "1",
    icon: Lightbulb,
    title: "Idee",
    description: "Alles beginnt mit Ihrer Idee. In einem persönlichen Gespräch entwickeln wir gemeinsam daraus die Basis für ein außergewöhnliches Video."
  },
  {
    number: "2",
    icon: Pencil,
    title: "Planung",
    description: "In der Planungsphase entwickle ich aus Ihrer Idee ein umsetzbares Konzept. Ich kümmere mich um Drehbuch, Locations und alle Details."
  },
  {
    number: "3",
    icon: Camera,
    title: "Produktion",
    description: "Mit modernster Technik und jahrelanger Erfahrung setze ich Ihr Projekt professionell um. Dabei achte ich auf jedes Detail."
  },
  {
    number: "4",
    icon: Clapperboard,
    title: "Postproduktion",
    description: "In der Postproduktion erwecke ich Ihr Material zum Leben. Durch sorgfältigen Schnitt, Farbkorrektur und Sounddesign entsteht ein harmonisches Ganzes."
  },
  {
    number: "5",
    icon: Sparkles,
    title: "Fertigstellung",
    description: "Nach finaler Abstimmung und eventuellen Anpassungen erhalten Sie Ihr fertiges Projekt in allen gewünschten Formaten."
  }
];

const ProcessStep = ({ number, icon: Icon, title, description, isVisible, onComplete, showLine }: {
  number: string;
  icon: any;
  title: string;
  description: string;
  isVisible: boolean;
  onComplete: () => void;
  showLine: boolean;
}) => {
  return (
    <motion.div
      className="relative text-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Number */}
      <div className="relative mb-8">
        <motion.div 
          className="w-24 h-24 mx-auto bg-zinc-900 rounded-full flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={isVisible ? { scale: 1 } : { scale: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <motion.span 
            className="text-4xl font-bold"
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
          >
            {number}
          </motion.span>
        </motion.div>
        {showLine && (
          <motion.div
            className="absolute top-1/2 left-[calc(50%+3rem)] w-full h-0.5 bg-zinc-800 -translate-y-1/2 hidden md:block origin-left"
            initial={{ scaleX: 0 }}
            animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          />
        )}
      </div>
      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <div className="flex justify-center mb-4">
          <Icon size={32} />
        </div>
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
      </motion.div>
    </motion.div>
  );
};

function Home() {
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const [processStarted, setProcessStarted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Intersection Observer für den Prozess-Bereich
  const [processRef, processInView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  // Sequenzielle Animation der Schritte
  useEffect(() => {
    if (processInView && !processStarted) {
      setProcessStarted(true);
      
      // Schritt für Schritt einblenden mit Verzögerung
      PROCESS_STEPS.forEach((_, index) => {
        setTimeout(() => {
          setVisibleSteps(prev => [...prev, index]);
        }, index * 600); // 600ms Verzögerung zwischen jedem Schritt
      });
    }
  }, [processInView, processStarted]);

  const handleStepComplete = () => {
    // Diese Funktion wird nicht mehr benötigt, aber wir behalten sie für die Kompatibilität
  };

  return (
    <>
      {/* Hero Section with Video */}
      <header className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-10 bg-black/40"></div>
        <video
          ref={videoRef}
          className="absolute w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={MEDIA_CONFIG.hero.video.poster}
        >
          <source src="https://honzafilm.de/media/videos/header.mp4" type="video/mp4" />
        </video>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-20 text-center px-4"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Visuelle Geschichten,<br />
            die bewegen
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
            {MEDIA_CONFIG.hero.video.subtitle}
          </p>
          <ContactButton text="Projekt starten" />
        </motion.div>

        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
        >
          <ChevronDown size={32} />
        </motion.div>
      </header>

      {/* Introduction */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
            Als Full-Service-Videoproduktion biete ich professionelle Film- und Videolösungen für Ihr Unternehmen. 
            Von der ersten Idee bis zum fertigen Projekt steht Ihnen meine langjährige Expertise in den Bereichen 
            Filmproduktion, Videografie und Postproduktion zur Verfügung.
          </p>
        </div>
      </section>

      {/* Showreel Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            {MEDIA_CONFIG.showreel.video.title}
          </h2>
          <div className="relative aspect-video">
            <VideoPlayer
              src="/media/videos/showreel.mp4"
              poster="/media/thumbnails/showreel-poster.jpg"
              showFullscreen={true}
              showQualitySelector={true}
              showTitle={false}
            />
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-zinc-900">
        <div ref={processRef} className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Der Weg zu Ihrem Projekt</h2>
          <div className="grid gap-8 md:grid-cols-5">
            {PROCESS_STEPS.map((step, index) => (
              <ProcessStep
                key={index}
                number={step.number}
                icon={step.icon}
                title={step.title}
                description={step.description}
                isVisible={visibleSteps.includes(index)}
                onComplete={() => handleStepComplete(index)}
                showLine={index < PROCESS_STEPS.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">Bereit für Ihr nächstes Projekt?</h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Lassen Sie uns gemeinsam Ihre Vision in ein eindrucksvolles visuelles Erlebnis verwandeln.
          </p>
          <ContactButton />
        </div>
      </section>
    </>
  );
}

export default Home;